// src/pages/admin/components/HomepageRequestsEdit.js
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Box, Grid, Card, CardHeader, CardContent, Avatar, Typography, Button,
  Divider, List, ListItem, ListItemAvatar, ListItemText, Chip, Snackbar,
  Alert, CircularProgress, IconButton, Tooltip, Select, MenuItem, Checkbox,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, InputAdornment,
  FormControl, InputLabel,
} from '@mui/material';
import InfoIcon        from '@mui/icons-material/Info';
import EmailIcon       from '@mui/icons-material/Email';
import PhoneIcon       from '@mui/icons-material/Phone';
import LinkIcon        from '@mui/icons-material/Link';
import StarIcon        from '@mui/icons-material/Star';
import PersonIcon      from '@mui/icons-material/Person';
import SearchIcon      from '@mui/icons-material/Search';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DoneIcon        from '@mui/icons-material/Done';
import OpenInNewIcon   from '@mui/icons-material/OpenInNew';
import SelectAllIcon   from '@mui/icons-material/SelectAll';
import DeselectIcon    from '@mui/icons-material/Deselect';

const API_URL = process.env.REACT_APP_API_URL || '';

// ─── Status-Konfiguration ────────────────────────────────────────────────────
const STATUS_OPTIONS = [
  'Angefragt',
  'In Bearbeitung',
  'Rückmeldung',
  'Erfolgreich',
  'Kein Verstoß',
];

// Reihenfolge der Status – Index bestimmt ob "rückwärts"
const STATUS_ORDER = {
  Angefragt:        1,
  'In Bearbeitung': 2,
  Rückmeldung:      3,
  Erfolgreich:      4,
  'Kein Verstoß':   5,
};

const STATUS_CHIP_COLOR = {
  Angefragt:        'warning',
  'In Bearbeitung': 'secondary',
  Rückmeldung:      'info',
  Erfolgreich:      'success',
  'Kein Verstoß':   'error',
};

// ─── Hilfsfunktionen ─────────────────────────────────────────────────────────
function stringAvatar(name) {
  if (!name) return { children: <PersonIcon /> };
  const initials = name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  return { children: initials };
}

function StarRating({ value, total = 5, size = 18 }) {
  if (value == null) return null;
  return (
    <span>
      {Array.from({ length: total }, (_, i) => (
        <StarIcon key={i} sx={{ color: i < value ? '#FFA726' : '#e0e0e0', fontSize: size, verticalAlign: 'middle' }} />
      ))}
    </span>
  );
}

function formatDate(d) {
  return d ? new Date(d).toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }) : '–';
}

function parseJsonField(val, fallback) {
  if (!val) return fallback;
  if (typeof val === 'object') return val;
  try { return JSON.parse(val); } catch { return fallback; }
}

// ─── Copy-Button ─────────────────────────────────────────────────────────────
function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <Tooltip title={copied ? 'Kopiert!' : 'In Zwischenablage kopieren'}>
      <IconButton size="small" onClick={handleCopy}>
        {copied ? <DoneIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
  );
}

// ─── Detail-Dialog ────────────────────────────────────────────────────────────
function DetailInfoDialog({ open, onClose, review }) {
  if (!review) return null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Bewertungsdetails</DialogTitle>
      <DialogContent dividers>
        <Box mb={2}>
          <Typography variant="subtitle2">Plattform</Typography>
          <Typography>{review.b_plattform || '–'}</Typography>
        </Box>
        <Box mb={2}>
          <Typography variant="subtitle2">Antwort vom Inhaber</Typography>
          <Typography>{review.responseFromOwnerText || '–'}</Typography>
        </Box>
        {review.reviewImageUrls?.length > 0 && (
          <Box mb={2}>
            <Typography variant="subtitle2">Bilder</Typography>
            <Box display="flex" gap={1} flexWrap="wrap" mt={0.5}>
              {review.reviewImageUrls.map((url, idx) => (
                <img key={idx} src={url} alt={`Bild ${idx + 1}`} width={80} style={{ borderRadius: 4 }} />
              ))}
            </Box>
          </Box>
        )}
        {review.reviewContext && Object.keys(review.reviewContext).length > 0 && (
          <Box mb={2}>
            <Typography variant="subtitle2">Kontext</Typography>
            {Object.entries(review.reviewContext).map(([k, v]) => (
              <Typography key={k} variant="body2"><b>{k}:</b> {v}</Typography>
            ))}
          </Box>
        )}
        {review.reviewDetailedRating && Object.keys(review.reviewDetailedRating).length > 0 && (
          <Box mb={2}>
            <Typography variant="subtitle2">Detailwertung</Typography>
            {Object.entries(review.reviewDetailedRating).map(([k, v]) => (
              <Typography key={k} variant="body2"><b>{k}:</b> {v}</Typography>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Schließen</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Bewertungskarte ──────────────────────────────────────────────────────────
const ReviewCard = React.memo(function ReviewCard({
  review, checked, onToggleCheck, selection, onChangeStatus, onOpenInfo,
}) {
  const currentStatus  = review.status;
  const selectedStatus = selection[review.b_id] ?? currentStatus ?? '';
  const isChecked      = checked.includes(review.b_id);

  // Warn wenn Status rückwärts gesetzt werden soll
  const currentOrder  = STATUS_ORDER[currentStatus]  ?? 0;
  const selectedOrder = STATUS_ORDER[selectedStatus] ?? 0;
  const isDowngrade   = selectedOrder > 0 && selectedOrder < currentOrder;

  const TEXT_LIMIT = 150;
  const [expanded, setExpanded] = useState(false);
  const text = review.text || '';

  return (
    <Card
      elevation={isChecked ? 3 : 1}
      sx={{
        height: '100%',
        border: isChecked ? '2px solid' : '1px solid transparent',
        borderColor: isChecked ? 'primary.main' : 'transparent',
        transition: 'border 0.15s, box-shadow 0.15s',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardHeader
        avatar={
          <Avatar
            src={review.reviewerPhotoUrl || ''}
            {...(!review.reviewerPhotoUrl && stringAvatar(review.name))}
            sx={{ bgcolor: '#ececec', width: 40, height: 40 }}
          />
        }
        title={
          <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
            <Typography fontWeight="bold" variant="body2">{review.name}</Typography>
            <StarRating value={review.rating} />
          </Box>
        }
        subheader={
          <Typography variant="caption" color="text.secondary">
            {formatDate(review.publishedAtDate)}
          </Typography>
        }
        action={
          <Box display="flex" alignItems="center">
            <Tooltip title="Details anzeigen">
              <IconButton size="small" onClick={() => onOpenInfo(review)}>
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={isChecked ? 'Abwählen' : 'Auswählen'}>
              <Checkbox
                size="small"
                checked={isChecked}
                onChange={() => onToggleCheck(review.b_id)}
              />
            </Tooltip>
          </Box>
        }
        sx={{ pb: 0 }}
      />
      <Divider />
      <CardContent sx={{ flexGrow: 1, pb: '8px !important' }}>
        {/* Bewertungstext */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {expanded ? text : text.slice(0, TEXT_LIMIT)}
          {text.length > TEXT_LIMIT && (
            <Button size="small" sx={{ ml: 0.5, p: 0, minWidth: 0 }} onClick={() => setExpanded(e => !e)}>
              {expanded ? 'weniger' : '...mehr'}
            </Button>
          )}
        </Typography>

        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mt={1}>
          {/* Aktueller Status */}
          {currentStatus && (
            <Chip
              label={currentStatus}
              size="small"
              color={STATUS_CHIP_COLOR[currentStatus] || 'default'}
            />
          )}

          {/* Neuer Status wählen */}
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status setzen</InputLabel>
            <Select
              value={selectedStatus}
              label="Status setzen"
              onChange={e => onChangeStatus(review.b_id, e.target.value)}
            >
              {STATUS_OPTIONS.map(opt => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Warnung bei Rückstufung */}
        {isDowngrade && (
          <Alert severity="warning" sx={{ mt: 1, py: 0, fontSize: '0.75rem' }}>
            Status wird zurückgesetzt ({currentStatus} → {selectedStatus})
          </Alert>
        )}
      </CardContent>
    </Card>
  );
});

// ─── Hauptkomponente ──────────────────────────────────────────────────────────
export default function HomepageRequestsEdit() {
  const [customers,        setCustomers]        = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [reviews,          setReviews]          = useState([]);
  const [checked,          setChecked]          = useState([]);
  const [selection,        setSelection]        = useState({});
  const [loading,          setLoading]          = useState(false);
  const [customerSearch,   setCustomerSearch]   = useState('');
  const [reviewFilter,     setReviewFilter]     = useState('all');
  const [infoDialog,       setInfoDialog]       = useState({ open: false, review: null });
  const [snackbar,         setSnackbar]         = useState({ open: false, message: '', severity: 'success' });

  const getHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  // ── Kundenliste laden ──
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API_URL}/api/review-requests/offers`, { headers: getHeaders() });
        setCustomers(data);
      } catch (err) {
        console.error(err);
        showSnackbar('Fehler beim Laden der Kundenliste.', 'error');
      } finally {
        setLoading(false);
      }
    })();
  }, [getHeaders, showSnackbar]);

  // ── Kunde + Bewertungen laden (async/await) ──
  const loadReviews = useCallback(async (cust) => {
    setSelectedCustomer(null);
    setChecked([]);
    setSelection({});
    setLoading(true);

    try {
      // Alle drei Requests parallel
      const [custRes, linkRes, reviewRes] = await Promise.allSettled([
        axios.get(`${API_URL}/api/infocustomer/${cust.id}`,  { headers: getHeaders() }),
        axios.get(`${API_URL}/api/unique-links/${cust.id}`,  { headers: getHeaders() }),
        axios.get(`${API_URL}/api/reviews/${cust.id}`,       { headers: getHeaders() }),
      ]);

      const customerData = custRes.status === 'fulfilled' ? custRes.value.data : cust;
      const linkData     = linkRes.status  === 'fulfilled' ? linkRes.value.data  : null;
      const rawReviews   = reviewRes.status === 'fulfilled' ? reviewRes.value.data : [];

      setSelectedCustomer({ ...customerData, linkData });

      const parsed = rawReviews.map(r => ({
        ...r,
        reviewImageUrls:      parseJsonField(r.reviewImageUrls,      []),
        reviewContext:        parseJsonField(r.reviewContext,        {}),
        reviewDetailedRating: parseJsonField(r.reviewDetailedRating, {}),
      }));
      setReviews(parsed);
    } catch (err) {
      console.error(err);
      showSnackbar('Fehler beim Laden der Bewertungen.', 'error');
    } finally {
      setLoading(false);
    }
  }, [getHeaders, showSnackbar]);

  // ── Gefilterte Kundenliste ──
  const filteredCustomers = useMemo(() => {
    if (!customerSearch) return customers;
    const q = customerSearch.toLowerCase();
    return customers.filter(c =>
      c.firmenname?.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q) ||
      c.name?.toLowerCase().includes(q)
    );
  }, [customers, customerSearch]);

  // ── Gefilterte Bewertungen ──
  const filteredReviews = useMemo(() => {
    if (reviewFilter === 'all') return reviews;
    return reviews.filter(r => r.status === reviewFilter);
  }, [reviews, reviewFilter]);

  // ── Checkbox-Logik ──
  const toggleCheck = useCallback(id => {
    setChecked(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }, []);

  const selectAll = useCallback(() => {
    setChecked(filteredReviews.map(r => r.b_id));
  }, [filteredReviews]);

  const deselectAll = useCallback(() => setChecked([]), []);

  const allSelected = filteredReviews.length > 0 && filteredReviews.every(r => checked.includes(r.b_id));

  // ── Status ändern (lokal) ──
  const changeStatus = useCallback((id, val) => {
    setSelection(prev => ({ ...prev, [id]: val }));
  }, []);

  // ── Status speichern: ein einziger API-Call ──
  const applyStatus = useCallback(async () => {
    // Nur Bewertungen mit tatsächlich gewähltem Status
    const toUpdate = checked.filter(id => {
      const newStatus = selection[id];
      return newStatus && STATUS_OPTIONS.includes(newStatus);
    });

    if (!toUpdate.length) {
      showSnackbar('Bitte wähle für die markierten Bewertungen einen neuen Status.', 'warning');
      return;
    }

    // Gruppieren nach Status (ein Call pro eindeutigem Status-Wert)
    const byStatus = toUpdate.reduce((acc, id) => {
      const st = selection[id];
      if (!acc[st]) acc[st] = [];
      acc[st].push(id);
      return acc;
    }, {});

    try {
      await Promise.all(
        Object.entries(byStatus).map(([status, ids]) =>
          axios.put(
            `${API_URL}/api/reviews/status`,
            { review_ids: ids, status },
            { headers: getHeaders() }
          )
        )
      );
      showSnackbar(`Status für ${toUpdate.length} Bewertung(en) aktualisiert.`, 'success');
      await loadReviews(selectedCustomer);
    } catch {
      showSnackbar('Fehler beim Aktualisieren des Status.', 'error');
    }
  }, [checked, selection, getHeaders, loadReviews, selectedCustomer, showSnackbar]);

  // ── Token-Link-URL ──
  const tokenLink = selectedCustomer?.linkData?.link_token
    ? `https://adm.sternefix.de/angebote/${selectedCustomer.linkData.link_token}`
    : null;

  const tokenExpired = selectedCustomer?.linkData?.expires_at
    ? new Date(selectedCustomer.linkData.expires_at) < new Date()
    : false;

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <Grid container sx={{ height: '100%', bgcolor: '#f5f7fa' }}>

      {/* ── Linke Spalte: Kundenliste ── */}
      <Grid
        item xs={12} sm={4}
        sx={{ p: 2, borderRight: { sm: '1px solid #e0e0e0' }, bgcolor: 'white', maxHeight: '100vh', overflowY: 'auto' }}
      >
        <Typography variant="h6" mb={1.5} fontWeight={700}>Kunden mit Anfragen</Typography>

        {/* Suchfeld */}
        <TextField
          size="small"
          fullWidth
          placeholder="Suchen..."
          value={customerSearch}
          onChange={e => setCustomerSearch(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
          }}
        />

        {loading && !selectedCustomer ? (
          <Box py={4} textAlign="center"><CircularProgress /></Box>
        ) : filteredCustomers.length === 0 ? (
          <Typography color="text.secondary" variant="body2">Keine Einträge gefunden.</Typography>
        ) : (
          <List dense disablePadding>
            {filteredCustomers.map(c => (
              <ListItem
                key={c.id}
                alignItems="flex-start"
                button
                selected={selectedCustomer?.id === c.id}
                onClick={() => loadReviews(c)}
                sx={{
                  borderRadius: 2, mb: 1,
                  '&.Mui-selected': { bgcolor: '#e3f2fd' },
                  '&.Mui-selected:hover': { bgcolor: '#bbdefb' },
                }}
              >
                <ListItemAvatar>
                  <Avatar
                    src={c.imageUrl || ''}
                    {...(!c.imageUrl && stringAvatar(c.firmenname))}
                    sx={{ bgcolor: '#e0e0e0', color: '#555' }}
                  />
                </ListItemAvatar>
                <ListItemText
                  primary={<Typography fontWeight="bold" variant="body2">{c.firmenname}</Typography>}
                  secondary={
                    <>
                      <Typography variant="caption" display="block" color="text.secondary">{c.email}</Typography>
                      <Chip label={`${c.anfragen} Anfrage(n)`} size="small" color="primary" variant="outlined" sx={{ mt: 0.5 }} />
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </Grid>

      {/* ── Rechte Spalte: Details + Bewertungen ── */}
      <Grid item xs={12} sm={8} sx={{ p: { xs: 1, sm: 3 }, overflowY: 'auto', minHeight: 0 }}>
        {selectedCustomer ? (
          <Grid container spacing={2}>

            {/* ── Kunden-Detailkarte ── */}
            <Grid item xs={12}>
              <Card elevation={2}>
                <CardHeader
                  avatar={
                    <Avatar
                      src={selectedCustomer.bild_url || ''}
                      {...(!selectedCustomer.bild_url && stringAvatar(selectedCustomer.firmenname))}
                      sx={{ width: 56, height: 56, fontSize: 24 }}
                    />
                  }
                  title={<Typography variant="h6" fontWeight="bold">{selectedCustomer.firmenname}</Typography>}
                  subheader={
                    <Box display="flex" flexWrap="wrap" gap={1} mt={0.5}>
                      <StarRating value={Math.round(selectedCustomer.bewertung)} />
                      <Typography variant="body2" color="text.secondary">
                        {selectedCustomer.bewertung} ({selectedCustomer.bewertungen_gesamt} Bewertungen)
                      </Typography>
                    </Box>
                  }
                  action={
                    selectedCustomer.business_url && (
                      <Tooltip title="Google-Unternehmensprofil öffnen">
                        <IconButton component="a" href={selectedCustomer.business_url} target="_blank" rel="noopener noreferrer">
                          <LinkIcon />
                        </IconButton>
                      </Tooltip>
                    )
                  }
                />
                <Divider />
                <CardContent>
                  <Grid container spacing={2}>
                    {/* Kontaktdaten */}
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary" mb={1}>Kontakt</Typography>
                      <Typography variant="body2" gutterBottom>
                        <b>Ansprechpartner:</b> {selectedCustomer.name || '–'}
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        <EmailIcon fontSize="inherit" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                        {selectedCustomer.email}
                        <CopyButton text={selectedCustomer.email} />
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        <PhoneIcon fontSize="inherit" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                        {selectedCustomer.telefonnummer || '–'}
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        <b>Adresse:</b> {selectedCustomer.adresse || '–'}
                      </Typography>
                      {selectedCustomer.gutscheincode && (
                        <Typography variant="body2">
                          <b>Gutscheincode:</b> {selectedCustomer.gutscheincode}
                        </Typography>
                      )}
                      <Typography variant="body2" mt={1} color="text.secondary">
                        Anfrage am: {formatDate(selectedCustomer.erstellungsdatum)}
                      </Typography>
                    </Grid>

                    {/* Token-Link */}
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary" mb={1}>Token-Link</Typography>
                      {tokenLink ? (
                        <Box>
                          <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                            <Chip
                              label={tokenExpired ? 'Abgelaufen' : (selectedCustomer.linkData?.used ? 'Geöffnet' : 'Noch nicht geöffnet')}
                              size="small"
                              color={tokenExpired ? 'error' : selectedCustomer.linkData?.used ? 'success' : 'default'}
                            />
                          </Box>
                          <Box
                            sx={{
                              bgcolor: '#f5f5f5', borderRadius: 1, px: 1.5, py: 0.75,
                              display: 'flex', alignItems: 'center', gap: 0.5,
                              border: '1px solid #e0e0e0',
                            }}
                          >
                            <Typography variant="caption" sx={{ flexGrow: 1, wordBreak: 'break-all', fontFamily: 'monospace' }}>
                              {tokenLink}
                            </Typography>
                            <CopyButton text={tokenLink} />
                            <Tooltip title="Link öffnen">
                              <IconButton size="small" component="a" href={tokenLink} target="_blank" rel="noopener noreferrer">
                                <OpenInNewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                            Erstellt: {formatDate(selectedCustomer.linkData?.created_at)} ·{' '}
                            Läuft ab: {formatDate(selectedCustomer.linkData?.expires_at)}
                          </Typography>
                          {selectedCustomer.linkData?.used_at && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Geöffnet: {formatDate(selectedCustomer.linkData.used_at)}
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">Noch kein Token-Link versandt.</Typography>
                      )}
                      <Typography variant="body2" mt={1.5} color="text.secondary">
                        E-Mail versandt: {selectedCustomer.email_versendet
                          ? formatDate(selectedCustomer.email_versanddatum)
                          : 'Nein'}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* ── Bewertungen ── */}
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} mb={1}>
                <Typography variant="h6" fontWeight={600}>
                  Angefragte Bewertungen ({reviews.length})
                </Typography>

                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  {/* Filter nach Status */}
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Filter</InputLabel>
                    <Select value={reviewFilter} label="Filter" onChange={e => setReviewFilter(e.target.value)}>
                      <MenuItem value="all">Alle</MenuItem>
                      {STATUS_OPTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                    </Select>
                  </FormControl>

                  {/* Alle/Keine auswählen */}
                  <Tooltip title={allSelected ? 'Alle abwählen' : 'Alle auswählen'}>
                    <IconButton size="small" onClick={allSelected ? deselectAll : selectAll}>
                      {allSelected ? <DeselectIcon /> : <SelectAllIcon />}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {loading ? (
                <Box py={6} textAlign="center"><CircularProgress /></Box>
              ) : filteredReviews.length === 0 ? (
                <Card><CardContent>
                  <Typography color="text.secondary">
                    {reviewFilter === 'all' ? 'Keine Bewertungen vorhanden.' : `Keine Bewertungen mit Status „${reviewFilter}".`}
                  </Typography>
                </CardContent></Card>
              ) : (
                <Grid container spacing={2}>
                  {filteredReviews.map(r => (
                    <Grid item xs={12} sm={6} md={4} key={r.b_id}>
                      <ReviewCard
                        review={r}
                        checked={checked}
                        onToggleCheck={toggleCheck}
                        selection={selection}
                        onChangeStatus={changeStatus}
                        onOpenInfo={rv => setInfoDialog({ open: true, review: rv })}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}

              {/* Status-Speichern-Button */}
              {reviews.length > 0 && (
                <Box mt={2} display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
                  {checked.length > 0 && (
                    <Typography variant="body2" color="text.secondary">
                      {checked.length} Bewertung(en) ausgewählt
                    </Typography>
                  )}
                  <Button
                    variant="contained"
                    onClick={applyStatus}
                    disabled={checked.length === 0}
                    startIcon={<DoneIcon />}
                  >
                    Status speichern
                  </Button>
                </Box>
              )}
            </Grid>
          </Grid>

        ) : (
          <Box
            display="flex" alignItems="center" justifyContent="center"
            sx={{ height: '60vh' }}
          >
            <Box textAlign="center">
              <PersonIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
              <Typography color="text.secondary" variant="h6">
                Wähle einen Kunden aus der Liste
              </Typography>
              <Typography color="text.disabled" variant="body2">
                Die Bewertungsanfragen und Details werden hier angezeigt.
              </Typography>
            </Box>
          </Box>
        )}
      </Grid>

      {/* ── Dialoge & Snackbar ── */}
      <DetailInfoDialog
        open={infoDialog.open}
        review={infoDialog.review}
        onClose={() => setInfoDialog({ open: false, review: null })}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Grid>
  );
}